import type {CreateExtArgs} from '@wix/document-manager-core'
import type {Pointer, CompRef} from '@wix/document-services-types'
import type {BuilderStyleExtensionAPI} from '../../builderComponents/builderStyle'
import type {ManifestExtensionAPI} from '../../builderComponents/manifest'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import type {ActionsAndBehaviorsExtensionAPI} from '../../actionsAndBehaviors'
import type {VariablesExtensionAPI} from '../../variables/variables'
import {
    RICH_TEXT_PROPERTY,
    type StyleData,
    type ComponentStyleData,
    type ThemeVariable,
    type ComponentFontData,
    type ExtractedFontData,
    type ComponentLogicIssue,
    type ComponentLogicData
} from './types'
import {DATA_TYPES} from '../../../constants/constants'
import * as constants from '../../../constants/constants'
import {traverseComponents} from './componentTraversal'

const isCssVariable = (value: string): boolean => value.startsWith('var(--') && value.endsWith(')')

const extractCssVarName = (cssVarString: string): string => cssVarString.slice(6, -1)

const stripHashIfExists = (value: string | undefined): string | undefined =>
    value?.startsWith('#') ? value.slice(1) : value

const extractStylePropertyValue = (rawValue: string | undefined, resolvedValue?: string): any | undefined => {
    if (!rawValue && !resolvedValue) {
        return undefined
    }
    if (rawValue && isCssVariable(rawValue)) {
        return {value: rawValue, cssVariable: extractCssVarName(rawValue), resolvedValue}
    }
    return {value: rawValue ?? resolvedValue ?? ''}
}

const extractColorsFromCssProps = (cssProperties: Record<string, any>): string[] => {
    const colors: string[] = []
    const colorProps = ['backgroundColor', 'color', 'borderColor', 'fill', 'stroke']

    for (const prop of colorProps) {
        const value = cssProperties[prop]
        if (value && typeof value === 'string' && (value.startsWith('#') || value.startsWith('rgb'))) {
            colors.push(value)
        }
    }
    return colors
}

const extractFontValue = (cssProperties: Record<string, any>): string | undefined => {
    if (cssProperties.font && typeof cssProperties.font === 'string') {
        return cssProperties.font
    }

    const {fontFamily, fontSize, fontWeight, fontStyle, lineHeight} = cssProperties

    if (fontFamily) {
        return typeof fontFamily === 'string' ? fontFamily : undefined
    }

    if (fontFamily || fontSize || fontWeight) {
        const parts = [
            fontStyle ?? 'normal',
            'normal',
            fontWeight ?? '400',
            fontSize ? `${fontSize}${lineHeight ? `/${lineHeight}` : ''}` : '',
            fontFamily ?? ''
        ].filter(Boolean)
        return parts.join(' ').trim() || undefined
    }

    return undefined
}

const extractInnerElementStyles = (
    componentData: Record<string, any>,
    dal: any
): {colors: string[]; textColor?: any} => {
    const colors: string[] = []
    let textColor: any

    const innerElementsQuery = stripHashIfExists(componentData?.innerElementsQuery)
    if (!innerElementsQuery) {
        return {colors}
    }

    const innerElementsMap = dal.get({id: innerElementsQuery, type: 'innerElements'})
    if (!innerElementsMap?.innerElements) {
        return {colors}
    }

    for (const [innerElementName, innerElementRef] of Object.entries(innerElementsMap.innerElements) as [
        string,
        string
    ][]) {
        const innerElementId = stripHashIfExists(innerElementRef)
        if (!innerElementId) {
            continue
        }

        const innerElement = dal.get({id: innerElementId, type: 'innerElements'})
        const styleId = stripHashIfExists(innerElement?.styleId)
        if (!styleId) {
            continue
        }

        let style = dal.get({id: styleId, type: 'style'})
        while (style?.type === 'RefArray' && style.values?.length > 0) {
            const refId = stripHashIfExists(style.values[0])
            if (!refId) {
                break
            }
            style = dal.get({id: refId, type: 'style'})
        }

        const cssProps = style?.cssProperties
        if (!cssProps) {
            continue
        }

        colors.push(...extractColorsFromCssProps(cssProps))

        if (cssProps.color && typeof cssProps.color === 'string' && !textColor) {
            const propValue = extractStylePropertyValue(cssProps.color, cssProps.color)
            if (propValue) {
                textColor = {...propValue, innerElementId: innerElementName}
            }
        }
    }

    return {colors, textColor}
}

const findNonVariableFontInStyleChain = (componentData: Record<string, any>, dal: any): string | undefined => {
    const styleId = stripHashIfExists(componentData?.styleId)
    if (!styleId) {
        return undefined
    }

    const style = dal.get({id: styleId, type: 'style'})
    const stylesToCheck: any[] = []

    if (style?.type === 'RefArray' && style.values?.length > 0) {
        for (const refValue of style.values) {
            const refId = stripHashIfExists(refValue)
            if (refId) {
                const refStyle = dal.get({id: refId, type: 'style'})
                if (refStyle) {
                    stylesToCheck.push(refStyle)
                }
            }
        }
    } else if (style) {
        stylesToCheck.push(style)
    }

    for (const styleToCheck of stylesToCheck) {
        const cssProps = styleToCheck.cssProperties
        if (!cssProps) {
            continue
        }

        if (cssProps.fontFamily && typeof cssProps.fontFamily === 'string' && !isCssVariable(cssProps.fontFamily)) {
            return cssProps.fontFamily
        }

        if (cssProps.font && typeof cssProps.font === 'string' && !isCssVariable(cssProps.font)) {
            const familyMatch = cssProps.font.match(/([a-zA-Z\s-]+)(?:,|$)/)
            if (familyMatch) {
                return familyMatch[1].trim()
            }
        }
    }

    return undefined
}

const extractComponentStyleData = (
    componentPointer: CompRef,
    builderStyleComponent: any,
    componentData: Record<string, any>,
    dal: any
): ComponentStyleData | null => {
    const style = builderStyleComponent.getCSSStyle(componentPointer)
    if (!style) {
        return null
    }

    const resolvedStyle = builderStyleComponent.getCSSStyleWithResolvedVariables?.(componentPointer)
    const cssProperties = (style as Record<string, any>).cssProperties ?? {}
    const resolvedCssProperties = (resolvedStyle as Record<string, any>)?.cssProperties ?? {}

    const colors = extractColorsFromCssProps(resolvedCssProperties)
    const innerElementStyles = extractInnerElementStyles(componentData, dal)
    colors.push(...innerElementStyles.colors)

    const nonVariableFont = findNonVariableFontInStyleChain(componentData, dal)
    let fontValue = extractFontValue(cssProperties)
    const resolvedFontValue = extractFontValue(resolvedCssProperties)

    if (nonVariableFont && !fontValue) {
        fontValue = nonVariableFont
    } else if (nonVariableFont && fontValue && isCssVariable(fontValue)) {
        fontValue = nonVariableFont
    }

    const backgroundColor = extractStylePropertyValue(
        cssProperties.backgroundColor,
        resolvedCssProperties.backgroundColor
    )
    const textColor =
        extractStylePropertyValue(cssProperties.color, resolvedCssProperties.color) || innerElementStyles.textColor

    return {
        componentId: componentPointer.id,
        colors,
        backgroundColor,
        textColor,
        font: extractStylePropertyValue(fontValue, resolvedFontValue)
    }
}

const extractFontsFromHTML = (html: string): string[] => {
    const fonts: Set<string> = new Set()
    const fontFamilyRegex = /font-family:\s*([^;}"']+)/gi
    const fontRegex = /font:\s*([^;}"']+)/gi

    let match
    while ((match = fontFamilyRegex.exec(html)) !== null) {
        const fontFamily = match[1].trim().replace(/['"]/g, '')
        if (fontFamily) {
            fonts.add(fontFamily)
        }
    }

    while ((match = fontRegex.exec(html)) !== null) {
        const fontValue = match[1].trim()
        const familyMatch = fontValue.match(/([a-zA-Z\s-]+)(?:,|$)/)
        if (familyMatch) {
            const fontFamily = familyMatch[1].trim().replace(/['"]/g, '')
            if (fontFamily && !/^\d/.test(fontFamily)) {
                fonts.add(fontFamily)
            }
        }
    }

    return Array.from(fonts)
}

const extractComponentFontData = (
    compPointer: Pointer,
    manifest: ManifestExtensionAPI['manifest']
): ComponentFontData | null => {
    let propsContext
    try {
        propsContext = manifest.getData(compPointer, true, [
            {
                prop: RICH_TEXT_PROPERTY,
                extract: (data: any) => data.text
            }
        ])
    } catch (e) {
        return null
    }

    for (const context of propsContext) {
        const {prop, value} = context
        const htmlContent = typeof value === 'string' ? value : value?.text

        if (prop === RICH_TEXT_PROPERTY && htmlContent) {
            const fonts = extractFontsFromHTML(htmlContent)
            if (fonts.length > 0) {
                return {
                    componentId: compPointer.id,
                    fonts
                }
            }
        }
    }

    return null
}

const checkButtonLink = (
    componentData: any,
    componentType: string,
    compPointer: Pointer,
    actionsAndBehaviors: any
): boolean => {
    const isButton = componentType.toLowerCase().includes('button')
    if (!isButton) {
        return true
    }

    const link = componentData?.data?.link || componentData?.link
    const href = componentData?.data?.href || componentData?.href

    if (link || href) {
        return true
    }

    if (actionsAndBehaviors) {
        const behaviors = actionsAndBehaviors.getBehaviors(compPointer)
        if (behaviors && behaviors.length > 0) {
            return true
        }
    }

    return false
}

const extractThemeVariables = (createExtArgs: CreateExtArgs): ThemeVariable[] => {
    const themeVariables: ThemeVariable[] = []
    const {extensionAPI, pointers} = createExtArgs
    const {variables, global} = extensionAPI as VariablesExtensionAPI

    if (!variables?.list || !global?.variables?.getVariableDataByCssVarName) {
        return themeVariables
    }

    for (const pointer of variables.list(pointers.structure.getMasterPage(constants.VIEW_MODES.DESKTOP))) {
        const varData = createExtArgs.dal.get(pointer)
        if (!varData?.cssVarName) {
            continue
        }
        const resolved = global.variables.getVariableDataByCssVarName(varData.cssVarName)
        if (resolved?.value) {
            themeVariables.push({cssVarName: varData.cssVarName, resolvedValue: resolved.value})
        }
    }

    return themeVariables
}

interface ExtractedData {
    style: StyleData
    fonts: ExtractedFontData
    componentLogic: ComponentLogicData
}

export const extractAllData = (createExtArgs: CreateExtArgs, containerPointer: Pointer): ExtractedData => {
    const {extensionAPI, dal} = createExtArgs
    const {builderStyle} = (extensionAPI as BuilderStyleExtensionAPI) ?? {}
    const {manifest} = extensionAPI as ManifestExtensionAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const actionsAndBehaviors = (extensionAPI as ActionsAndBehaviorsExtensionAPI)?.actionsAndBehaviors

    const componentStyles: ComponentStyleData[] = []
    const componentFonts: ComponentFontData[] = []
    const issues: ComponentLogicIssue[] = []

    traverseComponents(createExtArgs, containerPointer, (compPointer, type) => {
        const componentData = dal.get(compPointer)
        if (!componentData) {
            return
        }

        if (builderStyle?.component) {
            const styleData = extractComponentStyleData(
                compPointer as CompRef,
                builderStyle.component,
                componentData,
                dal
            )
            if (styleData) {
                componentStyles.push(styleData)
            }
        }

        const fontData = extractComponentFontData(compPointer, manifest)
        if (fontData) {
            componentFonts.push(fontData)
        }

        const dataPointer = dataModel.components.getItemPointer(compPointer, DATA_TYPES.data)
        const componentDataItem = dataPointer ? dal.get(dataPointer) : null

        if (!checkButtonLink(componentDataItem, type, compPointer, actionsAndBehaviors)) {
            issues.push({
                componentId: compPointer.id,
                componentType: type,
                issue: 'MISSING_LINK'
            })
        }
    })

    return {
        style: {
            componentStyles,
            themeVariables: extractThemeVariables(createExtArgs)
        },
        fonts: {
            componentFonts
        },
        componentLogic: {
            issues
        }
    }
}
